import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const Header = styled.header`
  text-align: center;
  color: white;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  font-size: 3rem;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  margin: 10px 0 0 0;
  opacity: 0.9;
`;

const BackButton = styled(Link)`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  text-decoration: none;
  color: white;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }
`;

const MainContent = styled.main`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const RightPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 20px;
  height: fit-content;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ff6b6b';
    if (props.$variant === 'warning') return '#ffd43b';
    return '#4ecdc4';
  }};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 30px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DPTable = styled.div`
  display: grid;
  gap: 2px;
  margin: 20px 0;
  max-width: 100%;
  overflow-x: auto;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 60px repeat(${props => props.$cols}, 1fr);
  gap: 2px;
`;

const TableCell = styled.div`
  background: ${props => {
    if (props.$isCurrent) return '#ffd43b';
    if (props.$isVisited) return '#4ecdc4';
    if (props.$isHeader) return '#667eea';
    return 'rgba(255, 255, 255, 0.9)';
  }};
  color: ${props => props.$isHeader ? 'white' : '#333'};
  padding: 12px 8px;
  border-radius: 5px;
  text-align: center;
  font-weight: ${props => props.$isHeader ? '600' : '500'};
  min-width: 50px;
  transition: all 0.3s ease;
  border: ${props => props.$isCurrent ? '2px solid #ff6b6b' : 'none'};
`;

const InfoPanel = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.h3`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1.2rem;
`;

const InfoText = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const StepInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StepTitle = styled.h4`
  color: white;
  margin: 0 0 10px 0;
  font-size: 1rem;
`;

const StepDescription = styled.p`
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0;
  font-size: 0.9rem;
`;

const AlgorithmSelector = styled.select`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 15px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const StatusBar = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
`;

const StatusText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

function DynamicProgrammingPage() {
  const [selectedAlgorithm, setSelectedAlgorithm] = useState('fibonacci');
  const [input, setInput] = useState('');
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [dpTable, setDpTable] = useState([]);
  const [stepHistory, setStepHistory] = useState([]);
  const [currentExplanation, setCurrentExplanation] = useState('');

  const algorithms = {
    fibonacci: {
      name: 'Fibonacci Sequence',
      description: 'Calculate the nth Fibonacci number using dynamic programming',
      inputPlaceholder: 'Enter n (e.g., 8)',
      complexity: 'Time: O(n), Space: O(n)'
    },
    lcs: {
      name: 'Longest Common Subsequence',
      description: 'Find the longest common subsequence between two strings',
      inputPlaceholder: 'Enter two strings (e.g., ABCDGH, AEDFHR)',
      complexity: 'Time: O(m*n), Space: O(m*n)'
    },
    knapsack: {
      name: '0/1 Knapsack',
      description: 'Solve the 0/1 knapsack problem using dynamic programming',
      inputPlaceholder: 'Enter weights and values (e.g., 1,3,4,5: 1,4,5,7)',
      complexity: 'Time: O(n*W), Space: O(n*W)'
    },
    editDistance: {
      name: 'Edit Distance',
      description: 'Find minimum operations to convert one string to another',
      inputPlaceholder: 'Enter two strings (e.g., horse, ros)',
      complexity: 'Time: O(m*n), Space: O(m*n)'
    }
  };

  const generateLCSTable = (str1, str2) => {
    const m = str1.length;
    const n = str2.length;
    const table = Array(m + 1).fill().map(() => Array(n + 1).fill(0));
    const steps = [];
    
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (str1[i-1] === str2[j-1]) {
          table[i][j] = table[i-1][j-1] + 1;
          steps.push({
            step: steps.length + 1,
            description: `Characters match: dp[${i}][${j}] = dp[${i-1}][${j-1}] + 1 = ${table[i][j]}`,
            table: JSON.parse(JSON.stringify(table)),
            current: [i, j]
          });
        } else {
          table[i][j] = Math.max(table[i-1][j], table[i][j-1]);
          steps.push({
            step: steps.length + 1,
            description: `Characters don't match: dp[${i}][${j}] = max(dp[${i-1}][${j}], dp[${i}][${j-1}]) = ${table[i][j]}`,
            table: JSON.parse(JSON.stringify(table)),
            current: [i, j]
          });
        }
      }
    }
    
    return { table, steps };
  };

  const generateKnapsackTable = (weights, values, capacity) => {
    const n = weights.length;
    const table = Array(n + 1).fill().map(() => Array(capacity + 1).fill(0));
    const steps = [];
    
    for (let i = 1; i <= n; i++) {
      for (let w = 0; w <= capacity; w++) {
        if (weights[i-1] <= w) {
          table[i][w] = Math.max(table[i-1][w], table[i-1][w - weights[i-1]] + values[i-1]);
          steps.push({
            step: steps.length + 1,
            description: `Item ${i}: include (${table[i-1][w - weights[i-1]] + values[i-1]}) or exclude (${table[i-1][w]})`,
            table: JSON.parse(JSON.stringify(table)),
            current: [i, w]
          });
        } else {
          table[i][w] = table[i-1][w];
          steps.push({
            step: steps.length + 1,
            description: `Item ${i}: too heavy, exclude: dp[${i}][${w}] = dp[${i-1}][${w}] = ${table[i][w]}`,
            table: JSON.parse(JSON.stringify(table)),
            current: [i, w]
          });
        }
      }
    }
    
    return { table, steps };
  };

  const generateFibonacciTableSimple = (n) => {
    const table = Array(n + 1).fill(0);
    const steps = [];
    
    table[0] = 0;
    table[1] = 1;
    
    steps.push({
      step: 1,
      description: 'Initialize: dp[0] = 0, dp[1] = 1',
      table: [...table],
      current: 1
    });
    
    for (let i = 2; i <= n; i++) {
      table[i] = table[i-1] + table[i-2];
      steps.push({
        step: i,
        description: `dp[${i}] = dp[${i-1}] + dp[${i-2}] = ${table[i-1]} + ${table[i-2]} = ${table[i]}`,
        table: [...table],
        current: i
      });
    }
    
    return { table, steps };
  };

  const handleVisualize = async () => {
    if (!input.trim() || isVisualizing) return;
    
    setIsVisualizing(true);
    setCurrentStep(0);
    setStepHistory([]);
    setCurrentExplanation('');
    
    let result;
    
    try {
      switch (selectedAlgorithm) {
        case 'fibonacci':
          const n = parseInt(input);
          if (isNaN(n) || n < 0) throw new Error('Please enter a valid positive number');
          result = generateFibonacciTableSimple(n);
          break;
          
        case 'lcs':
          const [str1, str2] = input.split(',').map(s => s.trim());
          if (!str1 || !str2) throw new Error('Please enter two strings separated by comma');
          result = generateLCSTable(str1, str2);
          break;
          
        case 'knapsack':
          const [weightsStr, valuesStr] = input.split(':').map(s => s.trim());
          if (!weightsStr || !valuesStr) throw new Error('Please enter weights and values separated by colon');
          const weights = weightsStr.split(',').map(w => parseInt(w.trim()));
          const values = valuesStr.split(',').map(v => parseInt(v.trim()));
          const capacity = Math.max(...weights) * 2; // Auto-calculate capacity
          if (weights.some(isNaN) || values.some(isNaN)) throw new Error('Please enter valid numbers');
          result = generateKnapsackTable(weights, values, capacity);
          break;
          
        case 'editDistance':
          const [s1, s2] = input.split(',').map(s => s.trim());
          if (!s1 || !s2) throw new Error('Please enter two strings separated by comma');
          result = generateEditDistanceTable(s1, s2);
          break;
          
        default:
          throw new Error('Unknown algorithm');
      }
      
      setDpTable(result.table);
      setStepHistory(result.steps);
      setTotalSteps(result.steps.length);
      
      // Start visualization
      for (let i = 0; i < result.steps.length; i++) {
        setCurrentStep(i + 1);
        setCurrentExplanation(result.steps[i].description);
        setDpTable(result.steps[i].table);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
    } catch (error) {
      alert(error.message);
    } finally {
      setIsVisualizing(false);
    }
  };

  const generateEditDistanceTable = (str1, str2) => {
    const m = str1.length;
    const n = str2.length;
    const table = Array(m + 1).fill().map(() => Array(n + 1).fill(0));
    const steps = [];
    
    // Initialize first row and column
    for (let i = 0; i <= m; i++) {
      table[i][0] = i;
      steps.push({
        step: steps.length + 1,
        description: `Initialize dp[${i}][0] = ${i}`,
        table: JSON.parse(JSON.stringify(table)),
        current: [i, 0]
      });
    }
    
    for (let j = 0; j <= n; j++) {
      table[0][j] = j;
      steps.push({
        step: steps.length + 1,
        description: `Initialize dp[0][${j}] = ${j}`,
        table: JSON.parse(JSON.stringify(table)),
        current: [0, j]
      });
    }
    
    // Fill the table
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (str1[i-1] === str2[j-1]) {
          table[i][j] = table[i-1][j-1];
          steps.push({
            step: steps.length + 1,
            description: `Characters match: dp[${i}][${j}] = dp[${i-1}][${j-1}] = ${table[i][j]}`,
            table: JSON.parse(JSON.stringify(table)),
            current: [i, j]
          });
        } else {
          const min = Math.min(table[i-1][j] + 1, table[i][j-1] + 1, table[i-1][j-1] + 1);
          table[i][j] = min;
          steps.push({
            step: steps.length + 1,
            description: `Characters don't match: dp[${i}][${j}] = min(${table[i-1][j]}+1, ${table[i][j-1]}+1, ${table[i-1][j-1]}+1) = ${min}`,
            table: JSON.parse(JSON.stringify(table)),
            current: [i, j]
          });
        }
      }
    }
    
    return { table, steps };
  };

  const renderTable = () => {
    if (!dpTable.length) return null;
    
    if (selectedAlgorithm === 'fibonacci') {
      return (
        <div>
          <h3 style={{ color: 'white', textAlign: 'center', marginBottom: '20px' }}>
            Fibonacci Table
          </h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
            {dpTable.map((value, index) => (
              <div
                key={index}
                style={{
                  background: currentStep === index + 1 ? '#ffd43b' : 'rgba(255, 255, 255, 0.9)',
                  color: currentStep === index + 1 ? '#333' : '#333',
                  padding: '15px',
                  borderRadius: '8px',
                  minWidth: '60px',
                  textAlign: 'center',
                  fontWeight: '600',
                  border: currentStep === index + 1 ? '2px solid #ff6b6b' : 'none'
                }}
              >
                <div style={{ fontSize: '0.8rem', marginBottom: '5px' }}>dp[{index}]</div>
                <div style={{ fontSize: '1.2rem' }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // For 2D tables
    const cols = dpTable[0]?.length || 0;
    
    return (
      <div>
        <h3 style={{ color: 'white', textAlign: 'center', marginBottom: '20px' }}>
          {algorithms[selectedAlgorithm].name} Table
        </h3>
        <DPTable>
          {dpTable.map((row, i) => (
            <TableRow key={i} $cols={cols}>
              {i === 0 ? (
                <>
                  <TableCell $isHeader>i\j</TableCell>
                  {Array.from({ length: cols }, (_, j) => (
                    <TableCell key={j} $isHeader>
                      {j}
                    </TableCell>
                  ))}
                </>
              ) : (
                <>
                  <TableCell $isHeader>{i - 1}</TableCell>
                  {row.map((cell, j) => (
                    <TableCell
                      key={j}
                      $isCurrent={currentStep > 0 && stepHistory[currentStep - 1]?.current?.[0] === i && stepHistory[currentStep - 1]?.current?.[1] === j}
                      $isVisited={currentStep > 0 && stepHistory.slice(0, currentStep).some(step => step.current[0] === i && step.current[1] === j)}
                    >
                      {cell}
                    </TableCell>
                  ))}
                </>
              )}
            </TableRow>
          ))}
        </DPTable>
      </div>
    );
  };

  return (
    <PageContainer>
      <BackButton to="/">← Back to Home</BackButton>
      
      <Header>
        <Title>Dynamic Programming Visualizer</Title>
        <Subtitle>Visualize common dynamic programming algorithms step by step</Subtitle>
      </Header>
      
      <MainContent>
        <LeftPanel>
          <VisualizationContainer>
            {renderTable()}
          </VisualizationContainer>
        </LeftPanel>
        
        <RightPanel>
          <ControlGroup>
            <Label>Select Algorithm</Label>
            <AlgorithmSelector
              value={selectedAlgorithm}
              onChange={(e) => setSelectedAlgorithm(e.target.value)}
            >
              {Object.entries(algorithms).map(([key, algo]) => (
                <option key={key} value={key}>{algo.name}</option>
              ))}
            </AlgorithmSelector>
            
            <Label>Input</Label>
            <Input
              type="text"
              placeholder={algorithms[selectedAlgorithm].inputPlaceholder}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            
            <Button
              onClick={handleVisualize}
              disabled={isVisualizing || !input.trim()}
            >
              {isVisualizing ? 'Visualizing...' : 'Start Visualization'}
            </Button>
            
            <Button
              $variant="warning"
              onClick={() => {
                setInput('');
                setDpTable([]);
                setStepHistory([]);
                setCurrentStep(0);
                setCurrentExplanation('');
              }}
            >
              Reset
            </Button>
          </ControlGroup>
          
          <InfoPanel>
            <InfoTitle>{algorithms[selectedAlgorithm].name}</InfoTitle>
            <InfoText>{algorithms[selectedAlgorithm].description}</InfoText>
            <InfoText><strong>Complexity:</strong> {algorithms[selectedAlgorithm].complexity}</InfoText>
          </InfoPanel>
          
          <StatusBar>
            <StatusText><strong>Progress:</strong> {currentStep} / {totalSteps}</StatusText>
            <StatusText><strong>Status:</strong> {isVisualizing ? 'Running...' : 'Ready'}</StatusText>
          </StatusBar>
          
          {currentExplanation && (
            <StepInfo>
              <StepTitle>Current Step</StepTitle>
              <StepDescription>{currentExplanation}</StepDescription>
            </StepInfo>
          )}
        </RightPanel>
      </MainContent>
    </PageContainer>
  );
}

export default DynamicProgrammingPage;
